import { type ReactNode } from 'react';
import { Box, Typography, alpha, useTheme } from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsHeatmap from 'highcharts/modules/heatmap';

import type { SxProps, Theme } from '@mui/material';

// Register the heatmap module once at import time. Idempotent in Highcharts 11.
HighchartsHeatmap(Highcharts);

interface EmmHeatmapOptionsArgs {
  xCategories: string[];
  yCategories: string[];
  cells: EmmHeatmapCell[];
  minColor: string | undefined;
  maxColor: string | undefined;
  showLabels: boolean;
  height: number;
  theme: Theme;
}

// Module-level factory so the `formatter()` `this` binding survives React
// Compiler optimization (a ThisExpression in the component body can't be
// compiled). `xCategories`/`yCategories` are threaded through so the formatter
// can resolve point labels.
const buildHeatmapOptions = ({
  xCategories,
  yCategories,
  cells,
  minColor,
  maxColor,
  showLabels,
  height,
  theme
}: EmmHeatmapOptionsArgs): Highcharts.Options => {
  const resolvedMin = minColor ?? theme.palette.background.surface;
  const resolvedMax = maxColor ?? theme.palette.primary.main;

  return {
    chart: {
      type: 'heatmap',
      backgroundColor: 'transparent',
      height,
      spacing: [8, 0, 8, 0],
      style: { fontFamily: theme.typography.fontFamily }
    },
    title: { text: undefined },
    legend: { enabled: false },
    credits: { enabled: false },
    accessibility: { enabled: false },
    xAxis: {
      categories: xCategories,
      lineWidth: 0,
      tickWidth: 0,
      labels: { style: { color: alpha(theme.palette.text.secondary, 0.7), fontSize: '11px' } }
    },
    yAxis: {
      categories: yCategories,
      title: { text: undefined },
      reversed: true,
      lineWidth: 0,
      tickWidth: 0,
      gridLineWidth: 0,
      labels: { style: { color: alpha(theme.palette.text.secondary, 0.7), fontSize: '11px' } }
    },
    colorAxis: {
      min: 0,
      minColor: resolvedMin,
      maxColor: resolvedMax
    },
    tooltip: {
      useHTML: true,
      backgroundColor: theme.palette.background.surface,
      borderColor: theme.palette.divider,
      borderRadius: 4,
      borderWidth: 1,
      padding: 8,
      shadow: true,
      style: {
        color: theme.palette.text.primary,
        fontFamily: theme.typography.fontFamily,
        fontSize: '12px',
        fontWeight: '400'
      },
      formatter() {
        const point = this.point as Highcharts.Point & { value?: number };
        const xLabel = xCategories[point.x as number] ?? '';
        const yLabel = yCategories[point.y as number] ?? '';
        return `<span style="opacity:0.6;font-size:11px">${yLabel} · ${xLabel}</span><br/><b style="font-weight:600">${
          point.value ?? 0
        }</b>`;
      }
    },
    plotOptions: {
      heatmap: {
        borderWidth: 0,
        pointPadding: 2,
        dataLabels: {
          enabled: showLabels,
          style: {
            color: theme.palette.text.primary,
            fontSize: '12px',
            fontWeight: '600',
            textOutline: 'none'
          }
        }
      }
    },
    series: [
      {
        type: 'heatmap',
        name: 'Heatmap',
        data: cells.map(c => (c.color ? { x: c.x, y: c.y, value: c.value, color: c.color } : [c.x, c.y, c.value])),
        turboThreshold: 0
      } as Highcharts.SeriesHeatmapOptions
    ]
  };
};

export interface EmmHeatmapCell {
  /** Column index (0-based, must align with `xCategories`). */
  x: number;
  /** Row index (0-based, must align with `yCategories`). */
  y: number;
  /** Numeric intensity. Drives the gradient color unless `color` is set. */
  value: number;
  /** Optional resolved color override — bypasses the value-based gradient. Use for risk matrices where position drives color. */
  color?: string;
}

export interface EmmHeatmapProps {
  xCategories: string[];
  yCategories: string[];
  cells: EmmHeatmapCell[];
  /** Resolved color at the low end of the gradient. Default `theme.palette.background.surface`. */
  minColor?: string;
  /** Resolved color at the high end of the gradient. Default `theme.palette.primary.main`. */
  maxColor?: string;
  /** Render the cell value inside each cell. Default `true`. */
  showLabels?: boolean;
  height?: number;
  title?: ReactNode;
  sx?: SxProps<Theme>;
}

export const EmmHeatmap = ({
  xCategories,
  yCategories,
  cells,
  minColor,
  maxColor,
  showLabels = true,
  height = 280,
  title,
  sx
}: EmmHeatmapProps) => {
  const theme = useTheme();

  const options = buildHeatmapOptions({
    xCategories,
    yCategories,
    cells,
    minColor,
    maxColor,
    showLabels,
    height,
    theme
  });

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 0.5, ...sx }}>
      {title != null && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {typeof title === 'string' ? (
            <Typography variant="subtitle2" color="text.primary">
              {title}
            </Typography>
          ) : (
            title
          )}
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        <HighchartsReact highcharts={Highcharts} options={options} immutable />
      </Box>
    </Box>
  );
};
